import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import ReportingPageLayout from './ReportingPageLayout';
import KpiCardGrid from './KpiCardGrid';
import useReportingPage from '../hooks/useReportingPage';
import { compactCurrency, compactCurrencyAxis, currencyFormatter, revenueBy } from '../utils/reportingUtils';
import { buildSalesBySourceFilterGroups } from '../constants/salesBySourceFiltersConfig';
import '../styles/salesBySource.css';

const SERIES_COLORS = [
  'var(--sl-fg-brand, #1982C4)', '#FAB714', '#8AC926', '#6A4C93', '#2CA8A0',
  '#F47C2C', 'var(--sl-text-quaternary, #6B7280)', '#E86A92',
];

const OTHER_SERIES_NAME = 'Other';
const EM_DASH = '—';

const AXIS_TICK = { fontSize: 12, fill: 'var(--sl-text-quaternary, #6B7280)' };
const TOOLTIP_LABEL_STYLE = { color: 'var(--sl-text-primary, #111827)', fontSize: 12 };
const TOOLTIP_CONTENT_STYLE = {
  borderRadius: 6,
  border: '1px solid var(--sl-border-secondary, #E5E7EB)',
  background: 'var(--sl-bg-primary, #fff)',
  fontSize: 12,
};
const TOOLTIP_ITEM_STYLE = { color: 'var(--sl-text-secondary, #374151)' };

const UTM_FILTER_KEYS = ['utm_sources', 'utm_mediums', 'utm_campaigns', 'utm_contents', 'utm_terms'];

const buildApiParams = (activeFilters, dateRange, comparison) => {
  const params = {};
  if (dateRange.start && dateRange.end) {
    params.start_date = dateRange.start;
    params.end_date = dateRange.end;
  }
  if (comparison && comparison.start && comparison.end) {
    params.prior_start_date = comparison.start;
    params.prior_end_date = comparison.end;
  }
  Object.entries(activeFilters).forEach(([key, value]) => {
    if (!Array.isArray(value) || value.length === 0) return;
    if (['venue_ownership_id', 'calendar_event_id', 'tag_ids', ...UTM_FILTER_KEYS].includes(key)) {
      params[key] = value;
    }
  });
  return params;
};

// The server already buckets sources into top-N + "Other" — see Reporting::SalesBySourceQuery#trend.
// This helper only flattens the per-date `sources` map into Recharts row shape.
const flattenTrend = (trendRows) => {
  if (!trendRows || trendRows.length === 0) return { data: [], seriesNames: [] };

  const seriesNames = Array.from(
    new Set(trendRows.flatMap((row) => Object.keys(row.sources || {})))
  );

  const data = trendRows.map((row) => {
    const flat = { date: row.date };
    seriesNames.forEach((name) => {
      flat[name] = (row.sources && row.sources[name] && row.sources[name].revenue) || 0;
    });
    return flat;
  });

  return { data, seriesNames };
};

const RevenueBreakdownCard = ({ title, data, loading }) => (
  <div className="sbs-panel sbs-panel--padded">
    <div className="sbs-panel__title mb-3">{title}</div>
    {data.length === 0 ? (
      <div className="sbs-empty">
        {loading ? 'Loading…' : 'No data for the selected range.'}
      </div>
    ) : (
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 32 + 40)}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--sl-border-secondary, #E5E7EB)" />
          <XAxis type="number" tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={compactCurrencyAxis} />
          <YAxis type="category" dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} width={120} />
          <Tooltip
            cursor={{ fill: 'var(--sl-bg-secondary, #F9FAFB)' }}
            formatter={(value) => [currencyFormatter.format(value), 'Revenue']}
            labelStyle={TOOLTIP_LABEL_STYLE}
            contentStyle={TOOLTIP_CONTENT_STYLE}
            itemStyle={TOOLTIP_ITEM_STYLE}
          />
          <Bar dataKey="revenue" fill={SERIES_COLORS[0]} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const formatCell = (value) => (value === null || value === undefined || value === '' ? EM_DASH : value);

const DeltaCell = ({ delta }) => {
  if (delta === null || delta === undefined) return EM_DASH;
  const isPositive = delta >= 0;
  const sign = isPositive ? '+' : '';
  return (
    <span className={isPositive ? 'sbs-delta--positive' : 'sbs-delta--negative'}>
      {sign}{delta.toFixed(1)}%
    </span>
  );
};

function ReportingSalesBySourcePage({
  team,
  csrfToken,
  venueOwnerships = [],
  eventOptions = [],
  tagOptions = [],
}) {
  const teamId = team && team.id;
  const reporting = useReportingPage('this_month');
  const { dateRange, activeFilters, comparison, isMobile } = reporting;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filtersKey = useMemo(() => JSON.stringify(activeFilters), [activeFilters]);

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    axios
      .get(`/teams/${teamId}/reporting/dashboard/sales-by-source/data.json`, {
        params: buildApiParams(activeFilters, dateRange, comparison),
        headers: { 'X-CSRF-Token': csrfToken },
      })
      .then((response) => { if (!cancelled) setData(response.data); })
      .catch(() => { if (!cancelled) setError('Failed to load sales by source data.'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [teamId, csrfToken, dateRange.start, dateRange.end, comparison && comparison.start, comparison && comparison.end, filtersKey]);

  const filterGroups = useMemo(
    () => buildSalesBySourceFilterGroups({
      venueOwnerships,
      eventOptions,
      tagOptions,
      utmFilterOptions: (data && data.filter_options) || {},
    }),
    [venueOwnerships, eventOptions, tagOptions, data && data.filter_options]
  );

  const cards = useMemo(() => {
    if (!data) return [];
    const k = data.kpis;
    return [
      { top: { label: 'Orders', value: (k.orders.current || 0).toLocaleString(), current: k.orders.current || 0, prior: k.orders.prior || 0, delta: k.orders.delta } },
      { top: { label: 'Tickets', value: (k.tickets.current || 0).toLocaleString(), current: k.tickets.current || 0, prior: k.tickets.prior || 0, delta: k.tickets.delta } },
      { top: { label: 'Revenue', value: compactCurrency(k.revenue.current || 0), current: k.revenue.current || 0, prior: k.revenue.prior || 0, delta: k.revenue.delta } },
      { top: { label: 'AOV', value: currencyFormatter.format(k.aov.current || 0), current: k.aov.current || 0, prior: k.aov.prior || 0, delta: k.aov.delta } },
    ];
  }, [data]);

  const { data: trendData, seriesNames } = useMemo(
    () => flattenTrend(data && data.trend),
    [data]
  );

  const rows = (data && data.rows) || [];
  const revenueBySource = useMemo(() => revenueBy(rows, 'utm_source'), [rows]);
  const revenueByMedium = useMemo(() => revenueBy(rows, 'utm_medium'), [rows]);
  const comparisonLabel = (comparison && comparison.label) || '';
  const topSourceCount = Math.max(seriesNames.length - (seriesNames.includes(OTHER_SERIES_NAME) ? 1 : 0), 0);

  return (
    <ReportingPageLayout
      team={team}
      activeKey="sales_by_source"
      title="Sales by Source"
      subtitle="Ticket sales attributed to UTM-tagged links."
      dateRange={dateRange}
      setDateRange={reporting.setDateRange}
      isMobile={isMobile}
      sidebarOpen={reporting.sidebarOpen}
      setSidebarOpen={reporting.setSidebarOpen}
      filterSidebarOpen={reporting.filterSidebarOpen}
      setFilterSidebarOpen={reporting.setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={reporting.handleFilterChange}
      handleRemoveFilter={reporting.handleRemoveFilter}
      handleClearAllFilters={reporting.handleClearAllFilters}
      filterTitle="Filters"
      filterSubtitle="Filter sales by venue, event, tag, or UTM tags."
    >
      {error && (
        <div className="alert alert-warning" role="alert">{error}</div>
      )}

      <KpiCardGrid
        loading={loading}
        isMobile={isMobile}
        columns={4}
        cards={cards}
        comparisonLabel={comparisonLabel}
      />

      <div className="sbs-panel sbs-panel--padded">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="sbs-panel__title">Revenue trend</div>
          <div className="sbs-panel__meta">Top {topSourceCount} sources</div>
        </div>
        {trendData.length === 0 ? (
          <div className="sbs-empty">
            {loading ? 'Loading…' : 'No data for the selected range.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <defs>
                {seriesNames.map((name, idx) => (
                  <linearGradient key={name} id={`utm-grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SERIES_COLORS[idx % SERIES_COLORS.length]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={SERIES_COLORS[idx % SERIES_COLORS.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--sl-border-secondary, #E5E7EB)" />
              <XAxis dataKey="date" tick={AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={compactCurrencyAxis} />
              <Tooltip
                formatter={(value) => currencyFormatter.format(value)}
                labelStyle={TOOLTIP_LABEL_STYLE}
                contentStyle={TOOLTIP_CONTENT_STYLE}
              />
              <Legend verticalAlign="top" align="right" iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingBottom: 8, color: 'var(--sl-text-secondary, #374151)' }} />
              {seriesNames.map((name, idx) => (
                <Area
                  key={name}
                  type="monotone"
                  dataKey={name}
                  name={name}
                  stackId="1"
                  stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                  strokeWidth={2}
                  fill={`url(#utm-grad-${idx})`}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="sbs-chart-row">
        <RevenueBreakdownCard title="Revenue by Source" data={revenueBySource} loading={loading} />
        <RevenueBreakdownCard title="Revenue by Medium" data={revenueByMedium} loading={loading} />
      </div>

      <div className="sbs-panel">
        <div className="sbs-panel__header">
          <div className="sbs-panel__title">Source breakdown</div>
        </div>
        {rows.length === 0 ? (
          <div className="sbs-empty">
            {loading ? 'Loading…' : 'No sales in the selected range.'}
          </div>
        ) : (
          <div className="sbs-table-wrapper">
            <table className="sbs-table">
              <thead>
                <tr>
                  <th scope="col">Source</th>
                  <th scope="col">Medium</th>
                  <th scope="col">Campaign</th>
                  <th scope="col">Content</th>
                  <th scope="col" className="sbs-cell--numeric">Orders</th>
                  <th scope="col" className="sbs-cell--numeric">Tickets</th>
                  <th scope="col" className="sbs-cell--numeric">Revenue</th>
                  <th scope="col" className="sbs-cell--numeric">AOV</th>
                  <th scope="col" className="sbs-cell--numeric">Δ Revenue</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr
                    key={`${row.utm_source}|${row.utm_medium}|${row.utm_campaign}|${row.utm_content}|${row.utm_term}|${idx}`}
                    className={row.is_direct ? 'sbs-row--direct' : ''}
                  >
                    <td>{formatCell(row.utm_source)}</td>
                    <td>{formatCell(row.utm_medium)}</td>
                    <td>{formatCell(row.utm_campaign)}</td>
                    <td>{formatCell(row.utm_content)}</td>
                    <td className="sbs-cell--numeric">{(row.orders_count || 0).toLocaleString()}</td>
                    <td className="sbs-cell--numeric">{(row.tickets_count || 0).toLocaleString()}</td>
                    <td className="sbs-cell--numeric">{compactCurrency(row.revenue || 0)}</td>
                    <td className="sbs-cell--numeric">{currencyFormatter.format(row.aov || 0)}</td>
                    <td className="sbs-cell--numeric"><DeltaCell delta={row.revenue_delta} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReportingPageLayout>
  );
}

export default ReportingSalesBySourcePage;
